import React, { useCallback, useEffect } from "react";
import BroadcastStore from "../_stores/broadcast/BroadcastStore";
import PricingStore from "common/broadcast/_stores/pricing/PricingStore";
import NodeChatStore from "../_stores/nodechat/NodeChatStore";
import { inject, observer } from "mobx-react";
import { BROADCAST_CHAT_INPUT_MAX_CHARS } from "./chat-tab";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindButton from "library/components/_tailwind/button";
import ChatStore from "common/broadcast/_stores/chat/ChatStore";
import BroadcastChatInputOptions from "./chat-input-options";
import classnames from "classnames";
import tw from "library/styles/tailwind-classnames";
import TextareaAutosize from "react-textarea-autosize";
import TailwindIcon from "library/components/_tailwind/icon";
import { CustomIconName } from "library/components/_tailwind/icon/icons/enums";
import { ChatModeEnum } from "../_stores/chat/enums";
import LanguageStore from "library/core/stores/language/LanguageStore";
import LayoutStore from "library/core/stores/layout/LayoutStore";

type Props = {
  broadcastStore?: BroadcastStore;
  pricingStore?: PricingStore;
  nodeChatStore?: NodeChatStore;
  chatStore?: ChatStore;
  sendMessage: (message: string) => void;
  setShowLimitMessage: (isOverLimit: boolean) => void;
  languageStore?: LanguageStore;
  layoutStore?: LayoutStore;
  disableInput: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  chatMode?: ChatModeEnum;
};

const BroadcastChatInput: React.ComponentType<Props> = ({
  chatStore,
  languageStore,
  layoutStore,
  sendMessage,
  disableInput,
  inputRef,
  pricingStore,
  nodeChatStore,
  chatMode,
}) => {
  const { message, setMessage } = chatStore!;
  const { isMobileDevice } = layoutStore!;
  const {
    activeMemberToWhisper,
    toggleOffWhisperTo,
    sendMessage: whipserSendMessage,
    whisperHash,
    activeChatTab,
    setSaveNextMessageToQT,
    saveNextMessageToQT,
    quickTexts,
  } = nodeChatStore!;
  const { modelProducts } = pricingStore!;
  const { intl } = languageStore!;

  // Auto-focus input when inline whisper is activated.
  const activeWhisperMemberId = activeMemberToWhisper?.id;
  useEffect(() => {
    if (!disableInput && activeWhisperMemberId) {
      inputRef.current?.focus();
    }
  }, [activeWhisperMemberId, disableInput, inputRef]);

  // Auto-focus input on mount of a whisper tab (separate-tabs mode).
  const shouldFocusOnMount = chatMode === ChatModeEnum.WHISPER && !disableInput;
  useEffect(() => {
    if (shouldFocusOnMount) {
      inputRef.current?.focus();
    }
  }, []);

  const handleChatInputChange = event => {
    const value = event.target.value;
    setMessage(value);
  };

  const handleChatMessageSend = async (event?) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (message === "") {
      return;
    }
    const messageToSend = message.substring(
      0,
      BROADCAST_CHAT_INPUT_MAX_CHARS - 1
    );

    sendMessage(messageToSend);
    setMessage("");
  };

  const whipserSendMsg = useCallback(
    (event?) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (message === "") {
        return;
      }
      whipserSendMessage(
        message,
        ChatModeEnum.WHISPER,
        activeMemberToWhisper?.username,
        activeMemberToWhisper?.id
      );
      setMessage("");
    },
    [activeMemberToWhisper, message]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.keyCode === 13) {
        if (!event.shiftKey) {
          event.preventDefault();
          event.stopPropagation();

          !modelProducts.chat_settings.system_settings
            .whispers_in_separate_tabs && activeMemberToWhisper?.id
            ? whipserSendMsg()
            : handleChatMessageSend();
        } else {
          event.preventDefault();
          const start = event.currentTarget.selectionStart;
          const end = event.currentTarget.selectionEnd;
          setMessage(
            `${message.substring(0, start)}\n${message.substring(end)}`
          );
          event.currentTarget.selectionStart =
            event.currentTarget.selectionEnd = start + 1;
          event.currentTarget.scrollTop = event.currentTarget.scrollHeight;
        }

        return false;
      }
      return true;
    },
    [handleChatMessageSend, message, setMessage]
  );

  return (
    <TailwindFlex
      htmlElementProps={{
        onSubmit:
          !modelProducts.chat_settings.system_settings
            .whispers_in_separate_tabs && activeMemberToWhisper?.id
            ? whipserSendMsg
            : handleChatMessageSend,
      }}
      as={"form"}
      flexDirection={"flex-col"}
      width={"w-full"}
      alignItems={"items-end"}>
      {/* Input row with send button on mobile */}
      <TailwindFlex
        width='w-full'
        flexDirection='flex-row'
        alignItems='items-end'
        gap={isMobileDevice ? "gap-2" : undefined}>
        <TailwindFlex
          flexDirection='flex-col'
          backgroundColor='bg-white'
          alignItems='items-end'
          padding={["pr-2"]}
          overflow='overflow-hidden'
          borderRadius={"rounded-xl"}
          flexGrow={isMobileDevice ? "flex-grow" : undefined}
          borderColor={
            !modelProducts.chat_settings.system_settings
              .whispers_in_separate_tabs && !!activeMemberToWhisper
              ? "border-pink-600"
              : "border-transparent"
          }
          borderWidth={["border"]}>
          <TailwindFlex
            flexDirection='flex-row'
            alignItems='items-center'
            justifyContent='justify-between'
            width='w-full'
            padding={["px-4", "py-2"]}>
            <label className='flex items-center text-sm text-gray-600'>
              <input
                type='checkbox'
                checked={saveNextMessageToQT}
                onChange={e => setSaveNextMessageToQT(e.target.checked)}
                className='mr-2'
                disabled={quickTexts.length >= 10}
              />
              {intl.formatMessage({
                id: "broadcast.chatInput.saveAsQuickText",
                defaultMessage: "Save as Quick Text",
              })}
            </label>
          </TailwindFlex>
          {!modelProducts.chat_settings.system_settings
            .whispers_in_separate_tabs && activeMemberToWhisper?.id ? (
            <div className='flex w-full'>
              <TailwindFlex
                backgroundColor='bg-pink-600'
                flex='flex-none'
                width='w-auto'
                padding={["px-3", "py-1"]}
                justifyContent='justify-center'
                alignItems='items-center'
                onClick={() => toggleOffWhisperTo(activeMemberToWhisper.id)}
                cursor={"cursor-pointer"}>
                <TailwindIcon
                  className='mr-1'
                  name={CustomIconName["private-message"]}
                  cursor={"cursor-pointer"}
                  textColor={"text-white"}
                  fontSize={"text-base"}
                />
                <span className='text-sm text-white'>
                  {activeMemberToWhisper.username}
                </span>
                <button className='ml-1'>
                  <TailwindIcon
                    name={"close"}
                    cursor={"cursor-pointer"}
                    textColor={"text-white"}
                    fontSize={"text-base"}
                  />
                </button>
              </TailwindFlex>
              <TextareaAutosize
                data-testid={"chat-input-field"}
                ref={inputRef}
                className={classnames(
                  "focus:outline-none",
                  "pretty-scroll",
                  tw(
                    "w-full",
                    "px-4",
                    "py-2",
                    "min-h-4",
                    "resize-none",
                    "text-black",
                    "bg-white",
                    "placeholder-gray-400"
                  )
                )}
                value={message}
                onChange={handleChatInputChange}
                onKeyDown={onKeyDown}
                data-is-enabled={!disableInput}
                disabled={disableInput}
                placeholder={
                  !disableInput
                    ? intl.formatMessage({
                        id: "broadcast.chatInputWhisperPlaceholder",
                        defaultMessage: "Type your whisper",
                      })
                    : intl.formatMessage({
                        id: "broadcast.chatInputOfflinePlaceholder",
                        defaultMessage: "Start broadcasting to chat",
                      })
                }
                rows={1}
                maxRows={3}
                maxLength={BROADCAST_CHAT_INPUT_MAX_CHARS}
              />
            </div>
          ) : (
            <TextareaAutosize
              data-testid={"chat-input-field"}
              ref={inputRef}
              className={classnames(
                "focus:outline-none",
                "pretty-scroll",
                tw(
                  "w-full",
                  "px-4",
                  "py-2",
                  "min-h-4",
                  "resize-none",
                  "text-black",
                  "bg-white",
                  "placeholder-gray-400"
                )
              )}
              value={message}
              onChange={handleChatInputChange}
              onKeyDown={onKeyDown}
              data-is-enabled={!disableInput}
              disabled={disableInput}
              placeholder={
                !disableInput &&
                !!whisperHash[activeChatTab] &&
                modelProducts.chat_settings.system_settings
                  .whispers_in_separate_tabs
                  ? intl.formatMessage({
                      id: "broadcast.chatInputWhisperPlaceholder",
                      defaultMessage: "Type your whisper",
                    })
                  : !disableInput
                  ? intl.formatMessage({
                      id: "broadcast.chatInputPlaceholder",
                      defaultMessage: "Type your message",
                    })
                  : intl.formatMessage({
                      id: "broadcast.chatInputOfflinePlaceholder",
                      defaultMessage: "Start broadcasting to chat",
                    })
              }
              rows={1}
              maxRows={3}
              maxLength={BROADCAST_CHAT_INPUT_MAX_CHARS}
            />
          )}
        </TailwindFlex>
      </TailwindFlex>

      {/* Mobile: Send button in same row as input */}
      {isMobileDevice && (
        <TailwindButton
          dataTestId={"chat-send-button"}
          fullWidth={true}
          rounded={false}
          margin={["mt-2"]}
          height={"h-12"}
          backgroundColor={"primary"}
          justifyContent='justify-center'
          type={"submit"}>
          {intl.formatMessage({
            id: "common.send",
            defaultMessage: "Send",
          })}
        </TailwindButton>
      )}

      {/* Mobile: Options below with horizontal scroll */}
      {isMobileDevice && (
        <TailwindFlex
          width='w-full'
          margin={["my-2"]}
          overflow={["overflow-x-auto"]}
          style={{ WebkitOverflowScrolling: "touch" }}>
          <BroadcastChatInputOptions inputRef={inputRef} />
        </TailwindFlex>
      )}
      {/* Desktop layout: Options and Send button in row */}
      {!isMobileDevice && (
        <TailwindFlex
          width='w-full'
          justifyContent='justify-between'
          margin={["mt-2"]}>
          <BroadcastChatInputOptions inputRef={inputRef} />
          <TailwindButton
            dataTestId={"chat-send-button"}
            fullWidth={false}
            rounded={false}
            padding={["px-8"]}
            height={"h-12"}
            backgroundColor={"primary"}
            type={"submit"}>
            {intl.formatMessage({
              id: "common.send",
              defaultMessage: "Send",
            })}
          </TailwindButton>
        </TailwindFlex>
      )}
    </TailwindFlex>
  );
};

export default inject(
  "broadcastStore",
  "chatStore",
  "pricingStore",
  "nodeChatStore",
  "languageStore",
  "layoutStore"
)(observer(BroadcastChatInput));
